import { MOCK_TOURNAMENT, MOCK_TOURNAMENT_TEAMS } from '@/features/marketing/mockData/tournament'

/**
 * Static still of the Tournament Stadium "GRAND FINAL" composition.
 *
 * Replaces the in-product Stadium component for the marketing page —
 * cheaper to load, sells the visual in a single frame, doesn't need
 * the polling + canvas layers that the live Stadium runs. A real
 * GIF / Lottie loop with the spark-burst can swap in later (per
 * MARKETING_EXPLORE_PLAN §10 decision 3) — this static still gives
 * us a punchy v1 mockup without that asset pipeline.
 *
 * Composition: header strip + GRAND FINAL banner + bracket strip +
 * VS focus card + ticker. Mimics the real layout proportions so
 * visitors recognise the screen when they land in the product.
 */
export function StadiumPreviewMockup() {
  const left = MOCK_TOURNAMENT_TEAMS.ORION
  const right = MOCK_TOURNAMENT_TEAMS.PALADIN
  return (
    <div className="relative flex aspect-video w-full flex-col overflow-hidden bg-[#06060f] text-white">
      {/* Atmospheric ray accents — captures the "spark burst settled"
          feel without animating. Pure SVG so it scales crisply. */}
      <svg
        viewBox="0 0 1600 900"
        className="pointer-events-none absolute inset-0 h-full w-full"
        aria-hidden
      >
        <defs>
          <linearGradient id="ray-1" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#3D6FFF" stopOpacity="0" />
            <stop offset="50%" stopColor="#3D6FFF" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#D946EF" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* 12 radial rays from centre — alternating primary blue / secondary purple */}
        {Array.from({ length: 24 }).map((_, i) => {
          const angle = (i / 24) * Math.PI * 2
          const x2 = 800 + Math.cos(angle) * 1100
          const y2 = 450 + Math.sin(angle) * 1100
          return (
            <line
              key={i}
              x1={800}
              y1={450}
              x2={x2}
              y2={y2}
              stroke={i % 2 === 0 ? '#3D6FFF' : '#D946EF'}
              strokeWidth="1.4"
              strokeOpacity="0.22"
            />
          )
        })}
        <circle cx="800" cy="450" r="320" fill="url(#ray-1)" opacity="0.55" />
      </svg>

      {/* Header strip */}
      <div className="relative flex items-center justify-between border-b border-white/10 bg-[#11112a]/80 px-6 py-3">
        <div className="min-w-0">
          <p className="truncate text-base font-black tracking-tight">
            {MOCK_TOURNAMENT.title}
          </p>
          <p className="font-display text-[10px] tracking-[0.22em] text-white/40 uppercase">
            {MOCK_TOURNAMENT.format}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-display inline-flex items-center gap-1.5 rounded-full border border-[#00ff88]/40 bg-[#00ff88]/15 px-3 py-1 text-[10px] font-extrabold tracking-[0.22em] text-[#00ff88] uppercase">
            <span className="size-1.5 rounded-full bg-[#00ff88]" />
            Live
          </span>
          <span className="font-display text-[10px] font-extrabold tracking-[0.25em] text-[#ffb300] uppercase">
            Grand Final
          </span>
        </div>
      </div>

      {/* GRAND FINAL banner */}
      <div className="relative flex items-center justify-center gap-4 py-4">
        <BannerRays direction="left" />
        <span
          className="font-display text-2xl font-black uppercase"
          style={{
            color: '#ffb300',
            letterSpacing: '0.35em',
            textShadow: '0 0 24px rgba(255,179,0,0.55)',
          }}
        >
          Grand Final
        </span>
        <BannerRays direction="right" />
      </div>

      {/* Main body — bracket strip + focus card. `flex-1 min-h-0`
          claims whatever vertical space the header/banner/ticker
          haven't taken, so the content never overflows the 16:9
          frame. `min-h-0` is critical here — without it the flex
          child refuses to shrink below intrinsic content height. */}
      <div className="relative flex min-h-0 flex-1 items-stretch gap-3 px-4 pb-3">
        {/* Bracket strip (narrow) */}
        <div className="flex w-1/4 flex-col gap-2 rounded-xl border border-[#1a1a2e]/80 bg-[#11112a]/60 p-3">
          <p className="font-display text-center text-[8px] font-extrabold tracking-[0.25em] text-white/40 uppercase">
            Bracket
          </p>
          {/* Mini bracket cards */}
          <div className="flex flex-1 flex-col justify-evenly gap-1">
            <MiniBracketMatch top="Orion" bottom="Saber" winner="top" />
            <MiniBracketMatch top="Stormbreaker" bottom="Cypher" winner="top" />
            <MiniBracketMatch top="Paladin" bottom="Juno" winner="top" />
            <MiniBracketMatch top="Tempest" bottom="Rook" winner="top" />
          </div>
        </div>

        {/* Focus card (wide) */}
        <div className="relative flex flex-1 flex-col items-center justify-center gap-3 overflow-hidden rounded-xl border-2 border-[#ffb300]/60 bg-[#11112a]/60 p-4 shadow-[0_0_40px_-10px_rgba(255,179,0,0.4)]">
          <p
            className="font-display text-[10px] font-extrabold tracking-[0.32em] uppercase"
            style={{ color: '#3D6FFF' }}
          >
            Final · Match 1
          </p>
          <div className="flex w-full items-center justify-between gap-4">
            <BigAvatar
              src={left.avatarUrl}
              name={left.teamLabel}
              side="left"
            />
            <VSChevron />
            <BigAvatar
              src={right.avatarUrl}
              name={right.teamLabel}
              side="right"
            />
          </div>
          <span
            className="font-display inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-extrabold tracking-[0.25em] uppercase"
            style={{
              color: '#00ff88',
              borderColor: '#00ff88',
              backgroundColor: 'rgba(0,255,136,0.1)',
            }}
          >
            <span className="size-1.5 rounded-full bg-[#00ff88]" />
            On now
          </span>
        </div>
      </div>

      {/* Ticker */}
      <div className="relative flex h-9 items-center gap-6 overflow-hidden border-t border-white/10 bg-[#11112a]/80 px-4">
        <span className="font-display text-[10px] font-extrabold tracking-[0.22em] text-[#3D6FFF] uppercase">
          Next
        </span>
        <span className="font-display text-[11px] font-semibold text-white">
          Orion vs Stormbreaker · in 6m
        </span>
        <span className="font-display text-[10px] font-extrabold tracking-[0.22em] text-white/40 uppercase">
          Result
        </span>
        <span className="font-display text-[11px] font-semibold text-white/80">
          Paladin beat Tempest
        </span>
      </div>
    </div>
  )
}

function MiniBracketMatch({
  top,
  bottom,
  winner,
}: {
  top: string
  bottom: string
  winner: 'top' | 'bottom'
}) {
  return (
    <div className="overflow-hidden rounded-md border border-white/10 bg-black/30">
      <div
        className="flex items-center justify-between px-2 py-1 text-[9px]"
        style={{
          opacity: winner === 'top' ? 1 : 0.5,
          backgroundColor: winner === 'top' ? 'rgba(0,255,136,0.08)' : 'transparent',
        }}
      >
        <span className="truncate font-display font-semibold text-white">{top}</span>
        {winner === 'top' && <span className="text-[#00ff88]">✓</span>}
      </div>
      <div className="h-px bg-white/5" />
      <div
        className="flex items-center justify-between px-2 py-1 text-[9px]"
        style={{
          opacity: winner === 'bottom' ? 1 : 0.5,
          backgroundColor: winner === 'bottom' ? 'rgba(0,255,136,0.08)' : 'transparent',
        }}
      >
        <span className="truncate font-display font-semibold text-white">{bottom}</span>
        {winner === 'bottom' && <span className="text-[#00ff88]">✓</span>}
      </div>
    </div>
  )
}

function BigAvatar({
  src,
  name,
  side,
}: {
  src: string
  name: string
  side: 'left' | 'right'
}) {
  const sideColour = side === 'left' ? '#3D6FFF' : '#D946EF'
  return (
    <div className="flex flex-1 flex-col items-center gap-2">
      <p
        className="font-display text-[9px] font-extrabold tracking-[0.3em] uppercase"
        style={{ color: sideColour }}
      >
        {side === 'left' ? 'Side A' : 'Side B'}
      </p>
      <div className="relative">
        <span
          aria-hidden
          className="absolute -inset-1 rounded-2xl blur-md"
          style={{ backgroundColor: sideColour, opacity: 0.4 }}
        />
        <img
          src={src}
          alt=""
          className="relative size-16 rounded-2xl object-cover"
          style={{ border: `3px solid ${sideColour}` }}
          loading="lazy"
        />
      </div>
      <p className="font-display text-sm font-extrabold tracking-tight text-white">
        {name}
      </p>
    </div>
  )
}

function VSChevron() {
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <span
        aria-hidden
        className="block h-1 w-5 skew-x-[-30deg]"
        style={{ backgroundColor: '#3D6FFF' }}
      />
      <span
        className="font-display text-3xl font-black tracking-tight text-white"
        style={{ textShadow: '0 0 20px rgba(0,0,0,0.7)' }}
      >
        VS
      </span>
      <span
        aria-hidden
        className="block h-1 w-5 skew-x-[-30deg]"
        style={{ backgroundColor: '#D946EF' }}
      />
    </div>
  )
}

function BannerRays({ direction }: { direction: 'left' | 'right' }) {
  return (
    <div
      aria-hidden
      className="flex items-center gap-1"
      style={{ transform: direction === 'right' ? 'scaleX(-1)' : undefined }}
    >
      <span className="block h-1 w-6 skew-x-[-30deg] bg-[#ffb300]/30" />
      <span className="block h-1 w-4 skew-x-[-30deg] bg-[#ffb300]/60" />
      <span className="block h-1 w-3 skew-x-[-30deg] bg-[#ffb300]" />
    </div>
  )
}
